// frontend/src/lib/stripeTerminalRecovery.js
//
// Stripe Terminal (Reader M2) — durable browser-restart recovery
// pointer for the card-present kiosk checkout flow. Sep 2026.
//
// Rationale
// ---------
// Identical to the problem `lib/cloverRecovery.js` solves: the kiosk
// runs in a browser on a physical tablet that customers can — and do —
// restart mid-payment. A minimal recovery HINT in `localStorage` lets a
// full restart land back on the same terminal session instead of
// orphaning it.
//
// Why this is a sibling module rather than a refactor of
// `cloverRecovery.js`
// -------------------------------------------------------------------
// The two are behaviourally identical apart from the storage key
// prefix. Generalising `cloverRecovery.js` into a factory would mean
// editing a working module that carries 29 passing unit tests, for a
// purely cosmetic gain, in a change whose whole premise is not to
// perturb the existing payment surfaces. The duplication is deliberate
// and bounded.
//
// Storage contract
// ----------------
// * Key:   `rudras.stripe.terminal.recovery.v1.<slug>`
// * Value: JSON { orderId, sessionId, slug, savedAt }
// * NEVER store: card data, PAN, cardholder name, amount, tender
//   details, PaymentIntent client secrets, tokens, tenant secrets, or
//   ANY payment authority. The backend is the SOLE authority on payment
//   state; this pointer is purely a "where do I resume?" hint. A
//   client secret in particular must never be persisted anywhere on
//   the browser — the device fetches it over its own authenticated
//   channel, per payment, and the kiosk never sees one at all.
// * TTL: pointers older than STALE_MS (24h) are ignored AND cleared on
//   read, so a stale entry cannot produce a recover → dismiss → recover
//   loop.
// * Tenant isolation: the key is namespaced by slug and the payload
//   also carries `slug`; a mismatch is treated as a miss.
//
// Framework-free (no React imports) so it is unit-testable in plain
// Node against a mock storage. Hook wiring lives in `CheckoutModal.jsx`.

export const RECOVERY_KEY_PREFIX = "rudras.stripe.terminal.recovery.v1.";
export const STALE_MS = 24 * 60 * 60 * 1000; // 24h

// Allow-list. Anything else a caller passes is dropped on write — a
// belt-and-braces guard against a future caller leaking amount/PAN/
// client-secret into localStorage.
const ALLOWED_FIELDS = ["orderId", "sessionId", "slug", "savedAt"];

export function buildRecoveryKey(slug) {
  if (!slug || typeof slug !== "string") return null;
  return `${RECOVERY_KEY_PREFIX}${slug}`;
}

/**
 * Persist a recovery pointer. Silently no-ops when storage is
 * unavailable, when `slug`/`orderId` are missing, or when `setItem`
 * throws (private browsing, quota, disabled storage).
 *
 * @param {Storage|null} storage e.g. window.localStorage
 * @param {string} slug tenant slug
 * @param {{ orderId: string, sessionId?: string|null, now?: number }} data
 * @returns {boolean} true if written
 */
export function saveRecovery(storage, slug, data) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return false;
  const orderId = data && data.orderId;
  if (!orderId || typeof orderId !== "string") return false;
  const sessionId =
    data && typeof data.sessionId === "string" && data.sessionId
      ? data.sessionId
      : null;
  const savedAt = typeof data?.now === "number" ? data.now : Date.now();
  const payload = { orderId, sessionId, slug, savedAt };
  try {
    storage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (_e) {
    return false;
  }
}

/**
 * Read the pointer for `slug`, or null when absent, tenant-mismatched,
 * malformed, or stale. A present-but-unusable entry is also CLEARED so
 * the caller cannot re-observe it on the next mount.
 *
 * @param {Storage|null} storage
 * @param {string} slug
 * @param {{ now?: number, staleMs?: number }} [opts]
 * @returns {{ orderId: string, sessionId: string|null, slug: string, savedAt: number }|null}
 */
export function readRecovery(storage, slug, opts) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return null;
  let raw;
  try {
    raw = storage.getItem(key);
  } catch (_e) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_e) {
    safeRemove(storage, key);
    return null;
  }
  if (!parsed || typeof parsed !== "object") {
    safeRemove(storage, key);
    return null;
  }
  if (parsed.slug !== slug || !parsed.orderId) {
    safeRemove(storage, key);
    return null;
  }
  const now = typeof opts?.now === "number" ? opts.now : Date.now();
  const staleMs = typeof opts?.staleMs === "number" ? opts.staleMs : STALE_MS;
  const savedAt = Number(parsed.savedAt);
  if (!Number.isFinite(savedAt) || now - savedAt > staleMs) {
    safeRemove(storage, key);
    return null;
  }
  const out = {};
  for (const f of ALLOWED_FIELDS) out[f] = parsed[f] ?? null;
  return out;
}

/** Remove the pointer for `slug`. Safe when none exists. */
export function clearRecovery(storage, slug) {
  const key = buildRecoveryKey(slug);
  if (!storage || !key) return;
  safeRemove(storage, key);
}

function safeRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch (_e) {
    // best-effort — nothing else we can do
  }
}
